import { renderChainOperands, enabledTaskKinds } from './chain';
import {
  pyChain,
  pyOperatorAssignment,
  pySingletonListEntry,
  pyTaskConfAssignment,
} from './pyFormat';
import { buildDatePredicate, scheduleGranularity } from './dates';
import {
  buildTargetRef,
  buildTaskName,
  pipelineFiles,
  taskVarNames,
} from './paths';
import {
  BQ_LOCATION,
  ColumnDef,
  DeleteScope,
  PipelineConfig,
  SLACK_ALERT_CONN,
  TableGroup,
  TaskKind,
} from './types';

/**
 * 파이프라인 → 저장소 파일들.
 *
 * ## 왜 Nunjucks 를 걷어냈는가
 *
 * 이전에는 렌더러가 둘이었다 — scaffolder 가 skeleton 파일을, 플러그인이 같은
 * 내용의 인라인 상수를 각각 Nunjucks 로 렌더했고, 두 벌이 어긋나지 않게
 * `skeletonParity.test.ts` 가 바이트 동일성을 강제했다.
 *
 * 새 규약에서는 그 구조가 성립하지 않는다. conf yaml 의 내용이 **계산 결과**
 * 이기 때문이다 — 삭제 쿼리의 날짜 식은 `schedule` 의 주기에 따라 달라지고,
 * DDL 은 컬럼 목록에서 만들어지고, task 키는 `.py` 의 조회 키와 글자 단위로
 * 같아야 하고, 테이블 그룹 수에 따라 파일 수와 체인 문장 수가 바뀐다.
 * 템플릿 문법으로 표현하려면 계산을 전부 values 로 미리 밀어넣어야 하는데,
 * 그러면 템플릿에 남는 것은 빈 칸뿐이고 Nunjucks 는 순전한 간접층이 된다.
 * 그래서 **렌더러를 하나로 줄이고 문자열 조립을 TS 에서 한다** — 생성과 수정이
 * 같은 함수를 부르므로 패리티 테스트가 필요 없다.
 */

export type RenderedFile = { path: string; content: string };

const indent = (text: string, spaces: number): string =>
  text
    .split('\n')
    .map(line => (line ? ' '.repeat(spaces) + line : ''))
    .join('\n');

/**
 * YAML 이중 인용 스칼라. 설명 문구에 `:` 나 `#` 이 들어가도 깨지지 않게 한다.
 * (스키마가 한 줄임을 보장하므로 개행 처리는 필요 없다.)
 */
const yamlQuoted = (value: string): string =>
  `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

/** `timedelta(...)` 식. 정시간이면 hours, 아니면 분으로 적어 의도를 읽기 쉽게 둔다. */
const pyTimedeltaHours = (hours: number): string =>
  Number.isInteger(hours)
    ? `timedelta(hours=${hours})`
    : `timedelta(minutes=${Math.round(hours * 60)})`;

const pyStartDate = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return `datetime(${y}, ${m}, ${d}, tzinfo=pendulum.timezone("Asia/Seoul"))`;
};

/** 파이프라인이 만드는 파일 경로들. 그룹 중 「넣기」 가 켜진 것만 SQL 을 갖는다. */
export const filesFor = (config: PipelineConfig) =>
  pipelineFiles({
    teamTag: config.team_tag,
    dagFolder: config.dag_folder,
    dagId: config.dag_id,
    insertTableIds: config.groups
      .filter(g => g.tasks.insert)
      .map(g => g.table_id),
  });

/** 그룹의 켜진 task 를 `(kind, group)` 쌍으로 평탄화한다. */
const taskEntries = (config: PipelineConfig) =>
  config.groups.flatMap(group =>
    enabledTaskKinds(group.tasks).map(kind => ({ kind, group })),
  );

// ---------------------------------------------------------------------------
// {dag_id}.py — 가이드 「단계 1 파일 ①」 과 같은 구조.
// ---------------------------------------------------------------------------

/**
 * import 는 **전부 명시**하고 Airflow 3 경로를 쓴다. dp-lakehouse 의 ruff
 * (`F`·`AIR3`)를 통과해야 PR 이 머지된다.
 *
 * 한때 가이드 정본을 따라 `from utils.common.comm_func import *` 로
 * `datetime`·`timedelta`·`pendulum`·`os` 를 받았다. 그 ruff 가 팀 디렉터리를
 * 대부분 `exclude` 하는데 **`dags-dp` 전체와 `dags-an` 의 `ds`·`lake`·`dw`·
 * `AN_archive` 밖은 검사**해서, 그 경로에 만든 DAG 이 F403·F405 와
 * AIR311(`airflow.DAG`)·AIR312(`airflow.operators.empty`)로 12 건 실패했다
 * (DPD-14021). 폴더는 사용자가 입력하므로 같은 AN 팀이라도 폴더에 따라 갈린다.
 * `dags-dp` 의 기존 DAG 190 개는 이미 star import 0 개다.
 *
 * 검사 제외 경로에서만 star import 를 쓰는 분기는 두지 않는다 — dp-lakehouse
 * 의 `exclude` 목록을 이 레포가 복사해 맞춰야 하고, 명시 import 는 어디서든
 * 통과한다. 또 AIR311·AIR312 경로는 ruff 와 무관하게 향후 Airflow 에서 제거된다.
 *
 * 쓰는 심볼은 템플릿에 고정돼 있다 — 설정마다 달라지는 부분은 `tasks[...]`,
 * `BigQueryInsertJobOperator`, `>>` 체인뿐이라 아래 import 가 모든 경우를 덮는다.
 * `utils.common.comm_func` 의 `get_cur_dir`·`load_config` 는 main·prod 양쪽에 있다.
 */
/*
 * 출력은 `ruff format` 이 고칠 것이 없는 모양이다 — dp-lakehouse CI 가
 * `ruff format --check` 를 돌린다. 이름 길이로 모양이 바뀌는 줄은 `pyFormat` 이 접는다.
 */
export function renderDagPy(config: PipelineConfig): string {
  const { team_tag: teamTag } = config;
  const entries = taskEntries(config);

  const varLines = entries.map(({ kind, group }) => {
    const { conf } = taskVarNames(kind, group.table_id);
    const key = buildTaskName({ teamTag, kind, tableId: group.table_id });
    return pyTaskConfAssignment(conf, key);
  });

  const operatorLines = entries.map(({ kind, group }) => {
    const { conf, operator } = taskVarNames(kind, group.table_id);
    // ⚠️ `durable=False` 를 빼면 **성공한 task 를 clear 해도 재실행되지 않는다**
    // (Airflow 3.3 부터 기본값이 durable=True, job id 캐시가 try_number 를
    // 포함하지 않음). 초록불만 다시 켜지고 데이터는 그대로다. dp-lakehouse 의
    // TestTeamBundleBigQueryJobDurability 가 누락을 막는다.
    return pyOperatorAssignment(operator, conf);
  });

  const chainLines = renderChainOperands(config.groups, config.group_execution).map(
    pyChain,
  );

  return `# 이 파일은 Backstage 가 생성·관리합니다 (파이프라인: ${config.dag_id}).
# 직접 고치면 다음 Backstage 저장 때 덮어쓰여질 수 있습니다.
import os
from datetime import datetime, timedelta

import pendulum
from airflow.providers.google.cloud.operators.bigquery import (
    BigQueryInsertJobOperator,
)
from airflow.providers.standard.operators.empty import EmptyOperator
from airflow.sdk import DAG
from airflow.timetables.interval import CronDataIntervalTimetable

from utils.common.comm_func import get_cur_dir, load_config
from utils.common.slack_alarm import SlackAlert

alert = SlackAlert("${SLACK_ALERT_CONN}")

default_dag_args = {
    "owner": "${config.owner}",
${pySingletonListEntry('email', config.owner_email)}
    "email_on_failure": False,
    "email_on_retry": False,
    "start_date": ${pyStartDate(config.start_date)},
    "retries": ${config.retries},
    "retry_delay": timedelta(minutes=${config.retry_delay_minutes}),
    "execution_timeout": ${pyTimedeltaHours(config.execution_timeout_hours)},
    "on_failure_callback": alert.task_fail_slack_alert,
}

cur_dir = get_cur_dir(__file__)
merged_config = load_config(
    os.path.join(cur_dir, "conf", os.path.basename(__file__)[:-3] + ".yaml")
)
dag_config = merged_config["dag_config"]
dag_config["schedule"] = CronDataIntervalTimetable(
    dag_config["schedule"], timezone="Asia/Seoul"
)
tasks = merged_config.get("tasks", {})

${varLines.join('\n')}

with DAG(
    default_args=default_dag_args,
    template_searchpath=[cur_dir, f"{cur_dir}/sql"],
    **dag_config,
) as dag:
    start_pipeline = EmptyOperator(task_id="START")
    finish_pipeline = EmptyOperator(task_id="END")

${operatorLines.join('\n')}

${chainLines.join('\n')}
`;
}

// ---------------------------------------------------------------------------
// conf/{dag_id}.yaml — 설정 전부가 모이는 파일.
// ---------------------------------------------------------------------------

/** `CREATE TABLE IF NOT EXISTS`. 컬럼 목록에서 만든다. */
export function renderCreateDdl(opts: {
  targetRef: string;
  columns: ColumnDef[];
  partitionBy: string;
}): string {
  const { targetRef, columns, partitionBy } = opts;
  const width = Math.max(...columns.map(c => c.name.length));
  const columnLines = columns.map((c, i) => {
    const nullability = c.required ? ' NOT NULL' : '';
    const comma = i === columns.length - 1 ? '' : ',';
    return `    ${c.name.padEnd(width)} ${c.type}${nullability}${comma}`;
  });
  // 파티션 없는 테이블은 `PARTITION BY` 를 붙이지 않는다 — 작은 매핑·차원
  // 테이블은 파티션이 의미가 없고, 억지로 날짜 컬럼을 만들게 하면 그 컬럼이
  // 아무 뜻도 없는 채로 남는다.
  if (!partitionBy) {
    return [
      `CREATE TABLE IF NOT EXISTS ${targetRef} (`,
      ...columnLines,
      ');',
    ].join('\n');
  }
  const partitionColumn = columns.find(c => c.name === partitionBy);
  // DATE 는 그대로, DATETIME·TIMESTAMP 는 DATE() 로 감싼다 — BigQuery 가
  // 날짜 파티션에 요구하는 형태이고, 틀리면 CREATE 가 실패한다.
  const partitionExpr =
    partitionColumn && partitionColumn.type !== 'DATE'
      ? `DATE(${partitionBy})`
      : partitionBy;
  return [
    `CREATE TABLE IF NOT EXISTS ${targetRef} (`,
    ...columnLines,
    ')',
    `PARTITION BY ${partitionExpr};`,
  ].join('\n');
}

/**
 * 「지우기」 쿼리. 날짜 식은 `schedule` 의 주기가 정한다.
 *
 * `scope: 'all'` 은 `WHERE TRUE` 다 — BigQuery 는 DELETE 에 WHERE 를 요구하고,
 * 전체 삭제의 표준 표기가 그것이다. 파티션 없는 작은 테이블의 전체 재적재에 쓴다.
 */
export function renderDeleteQuery(opts: {
  targetRef: string;
  scope: DeleteScope;
  dateColumn: string;
  schedule: string;
  lookbackDays: number;
}): string {
  const predicate =
    opts.scope === 'all'
      ? 'TRUE'
      : buildDatePredicate({
          column: opts.dateColumn,
          granularity: scheduleGranularity(opts.schedule),
          lookbackDays: opts.lookbackDays,
        });
  return [`DELETE FROM ${opts.targetRef}`, ` WHERE ${predicate}`].join('\n');
}

/**
 * task 블록. 쿼리는 전부 **블록 스칼라(`|-`)** 로 적는다 — 인용 없이 어떤
 * 내용도 안전하고, 가이드가 single-quote 와 `''` 이스케이프로 처리하던 부분에서
 * 오타가 날 여지를 없앤다.
 */
function renderTaskBlock(opts: {
  key: string;
  query: string;
  withLocation: boolean;
  gcpConnId?: string;
}): string {
  const lines = [`  ${opts.key}:`, `    task_id: ${opts.key}`];
  if (opts.withLocation) lines.push(`    location: ${BQ_LOCATION}`);
  if (opts.gcpConnId) lines.push(`    gcp_conn_id: ${opts.gcpConnId}`);
  lines.push(
    '    configuration:',
    '      query:',
    '        query: |-',
    indent(opts.query, 10),
    '        useLegacySql: false',
  );
  return lines.join('\n');
}

export function renderConfYaml(config: PipelineConfig): string {
  const { team_tag: teamTag } = config;
  const files = filesFor(config);
  const includeOf = new Map(files.sqlPaths.map(s => [s.tableId, s.include]));

  const queryFor = (kind: TaskKind, group: TableGroup): string => {
    const targetRef = buildTargetRef(group.table_id);
    if (kind === 'create') {
      return renderCreateDdl({
        targetRef,
        columns: group.tasks.create!.columns,
        partitionBy: group.tasks.create!.partition_by,
      });
    }
    if (kind === 'delete') {
      return renderDeleteQuery({
        targetRef,
        scope: group.tasks.delete!.scope,
        dateColumn: group.tasks.delete!.date_column,
        schedule: config.schedule,
        lookbackDays: group.tasks.delete!.lookback_days,
      });
    }
    return `{% include '${includeOf.get(group.table_id)}' %}`;
  };

  const taskBlocks = taskEntries(config).map(({ kind, group }) =>
    renderTaskBlock({
      key: buildTaskName({ teamTag, kind, tableId: group.table_id }),
      query: queryFor(kind, group),
      // 가이드는 생성·넣기 task 에만 location 을 적는다. DELETE 는 DML 이라
      // BigQuery 가 데이터셋에서 위치를 유추한다.
      withLocation: kind !== 'delete',
      gcpConnId: config.gcp_conn_id,
    }),
  );

  return `# 이 파일은 Backstage 가 생성·관리합니다 (파이프라인: ${config.dag_id}).
dag_config:
  dag_id: ${config.dag_id}
  description: ${yamlQuoted(config.description)}
  schedule: ${yamlQuoted(config.schedule)}
  catchup: false
  user_defined_macros:
    PROJECT_ID: ${config.target.project_id}
    DATA_SET: ${config.target.data_set}
  tags:
${config.tags.map(t => `    - ${t}`).join('\n')}

tasks:
${taskBlocks.join('\n')}
`;
}

// ---------------------------------------------------------------------------
// sql/{table_id}.sql — 사용자 SQL 전문. 내용을 고치지 않는다.
// ---------------------------------------------------------------------------

export function renderSqlFile(opts: {
  dagId: string;
  group: TableGroup;
}): string {
  const sql = opts.group.tasks.insert!.sql.replace(/\s*$/, '');
  return `-- 이 파일의 SQL 은 Backstage 수정 폼에서 관리합니다 (파이프라인: ${opts.dagId}).\n${sql}\n`;
}

// ---------------------------------------------------------------------------

/**
 * 파이프라인이 만드는 DAG 저장소 파일 전부.
 *
 * **프론트엔드도 이 함수를 부른다** — 생성·수정 전에 실제로 커밋될 파일을
 * 그대로 보여주려면 미리보기와 커밋이 같은 함수를 써야 한다. 서버에
 * `/preview` 라우트를 두는 쪽은 왕복이 생기고, 두 구현이 갈라질 여지가 남는다.
 */
export function pipelineFileContents(config: PipelineConfig): RenderedFile[] {
  const files = filesFor(config);
  const groupOf = new Map(config.groups.map(g => [g.table_id, g]));
  return [
    { path: files.pyPath, content: renderDagPy(config) },
    { path: files.confPath, content: renderConfYaml(config) },
    ...files.sqlPaths.map(({ tableId, path }) => ({
      path,
      content: renderSqlFile({
        dagId: config.dag_id,
        group: groupOf.get(tableId)!,
      }),
    })),
  ];
}
